import { useEffect, useMemo, useState } from 'react';
import cmitLogo from '../../assets/cmit-logo.png';
import { BarList, Segmented } from '../../components/data';
import { Empty, ErrorBanner, Field, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { budgetByYear, costSummaryText } from '../../lib/amCosts';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { formatDate, formatNumber } from '../../lib/format';
import { useHeldResource, useResource } from '../../lib/hooks';
import type { AmRecommendation } from '../../lib/types';
import { useFitToSheet } from './AmBusinessCase';

/* The Assets and Services tabs' reads (routes/amAutotask.ts) — only the fields used here. */
interface Asset {
  id: number;
  name: string;
  hostname: string | null;
  type: string | null;
  product: string | null;
  lastUser: string | null;
  contact: string | null;
  installed: string | null;
  warrantyExpires: string | null;
  active: boolean;
}
interface AssetsPayload {
  linked: boolean;
  companyName: string | null;
  assets: Asset[];
}
interface ContractService {
  id: number;
  contractId: number;
  contract: string;
  service: string;
  period: string | null;
  units: number | null;
  unitPrice: number | null;
  total: number | null;
}
interface ServicesPayload {
  linked: boolean;
  companyName: string | null;
  services: ContractService[];
}

/** What is kept in a saved budget (coo.AmSavedBudget, routes/amBudget.ts). */
interface Settings {
  startYear: number;
  /** include: false leaves the whole type out of the budget; absent means in. */
  types: Record<string, { lifecycleYears: number | null; price: number | null; include?: boolean }>;
  assets: Record<string, { price?: number | null; replaceYear?: number | null }>;
  services: Record<string, { include: boolean; increasePct?: number | null }>;
}
/** A saved budget as listed; opened, it carries its settings too. */
interface SavedBudget {
  id: string;
  name: string;
  startYear: number | null;
  createdBy: string | null;
  createdUtc: string;
  updatedBy: string | null;
  updatedUtc: string;
  settings?: Settings;
}

const YEARS = 5;
/* Every contract service rises this much a year, compounding, unless a
   service is given its own rate (0 for none). Saved with each service. */
const SERVICE_RISE = 3;
const UNTYPED = 'No type';
const money = (v: number) => formatNumber(v, 'currency');
const savedOn = (iso: string) => new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'short', year: 'numeric' }) : '—';
/* A year's figure from a five-long row; the index is always in range. */
const at = (row: number[], i: number) => row[i] ?? 0;
const numOrNull = (v: string) => {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* A starting point for a type nobody has set yet — workstations turn over
   faster than servers and network gear. Shown in the field, so it is a value
   somebody can see and change, not a hidden assumption. */
function suggestedLifecycle(type: string): number {
  if (/server|host|nas|san|storage/i.test(type)) return 6;
  if (/firewall|switch|router|access point|wireless|network|ups/i.test(type)) return 5;
  if (/workstation|desktop|laptop|notebook|pc|computer|tablet|surface/i.test(type)) return 4;
  return 5;
}

/** How many times a billing period falls in a year. One-time is not recurring. */
function perYearOf(period: string | null): number {
  const p = (period ?? 'Monthly').toLowerCase();
  if (/semi/.test(p)) return 2;
  if (/quarter/.test(p)) return 4;
  if (/year|annual/.test(p)) return 1;
  if (/one|once/.test(p)) return 0;
  return 12;
}

function defaultStartYear() {
  /* Budgets are written in the autumn for the year ahead. */
  const now = new Date();
  return now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear();
}

/**
 * A recommendation in or out of the budget, and the year it goes in — the
 * same control on the Recommendations tab and the Budget tab. It saves at
 * once (PUT /am/recommendations/:id with budgetYear), as the status dropdown
 * beside it does: it is a fact about the recommendation, not one of the
 * budget's unsaved settings.
 */
export function BudgetYearPicker({
  rec,
  onSaved,
  years,
}: {
  rec: AmRecommendation;
  onSaved: () => void;
  /** The years to offer; defaults to this year and the next five. */
  years?: number[];
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const year = rec.BudgetYear ?? null;
  const offered = years ?? Array.from({ length: 6 }, (_, i) => new Date().getFullYear() + i);
  /* A year set before the offered range is still shown rather than silently
     reading as another year. */
  const options = year !== null && !offered.includes(year) ? [year, ...offered].sort() : offered;
  const save = async (budgetYear: number | null) => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/am/recommendations/${rec.AmRecommendationId}`, { budgetYear });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="row" style={{ gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'nowrap' }}>
      <input
        type="checkbox"
        aria-label={`Add ${rec.Title ?? 'this recommendation'} to the budget`}
        title="In the budget"
        checked={year !== null}
        disabled={busy}
        onChange={(e) => void save(e.target.checked ? defaultStartYear() : null)}
      />
      <select
        className="select input-sm"
        aria-label={`Budget year for ${rec.Title ?? 'this recommendation'}`}
        value={year ?? ''}
        disabled={busy || year === null}
        onChange={(e) => void save(Number(e.target.value))}
      >
        {year === null && <option value="">—</option>}
        {options.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      {error && <span className="xs" style={{ color: 'var(--status-danger)' }}>{error}</span>}
    </span>
  );
}

/** The years a device is replaced in, within the plan, and the year it fell due. */
function plan(asset: Asset, lifecycle: number | null, override: number | null | undefined, start: number) {
  const end = start + YEARS - 1;
  if (override === 0) return { years: [] as number[], due: null as number | null, overdue: false, unknown: false };
  let first: number | null = null;
  let due: number | null = null;
  if (override) first = override;
  else if (asset.installed && lifecycle) {
    const d = new Date(`${asset.installed}T00:00:00`);
    due = Math.floor(d.getFullYear() + d.getMonth() / 12 + lifecycle);
    first = Math.max(due, start);
  }
  if (first === null) return { years: [], due, overdue: false, unknown: true };
  const years: number[] = [];
  /* A replaced device is replaced again when the new one reaches the same age. */
  for (let k = 0; ; k++) {
    const y = lifecycle ? Math.floor(first + k * lifecycle) : first;
    if (y > end || (k > 0 && !lifecycle)) break;
    if (y >= start) years.push(y);
    if (!lifecycle) break;
  }
  return { years, due, overdue: !override && due !== null && due < start, unknown: false };
}

/**
 * Account Management › Budget: a five-year plan for the client.
 *
 * - Every active asset (Autotask configuration item, the Assets tab's read),
 *   replaced when it reaches the lifecycle set for its type — counted from
 *   its install date, and again when its replacement reaches the same age.
 *   One already past it falls in the first year. A device can be given its
 *   own year, or left out, and its own price.
 * - Every service on the active contracts (the Services tab's read) as a
 *   yearly cost, with an expected rise per year.
 *
 * Lifecycle and price per type, a device's own price and year, and the
 * services' settings are saved for the client; the assets and services
 * themselves are read live from Autotask, so a new device shows up in the
 * plan without anybody adding it. Nothing goes to Autotask.
 */
export function AmBudget({
  clientId,
  clientName,
  organizationName,
  recommendations,
  reload,
}: {
  clientId: string;
  clientName: string;
  organizationName: string;
  /** The client's recommendations, from the workspace's own load. */
  recommendations: AmRecommendation[];
  /** Re-reads the workspace, after a recommendation is put in or taken out. */
  reload: () => void;
}) {
  const assetsRes = useHeldResource<AssetsPayload>(`/am/clients/${clientId}/autotask/assets`);
  const servicesRes = useHeldResource<ServicesPayload>(`/am/clients/${clientId}/autotask/services`);
  const saved = useResource<SavedBudget[]>(`/am/clients/${clientId}/budgets`);

  const [settings, setSettings] = useState<Settings | null>(null);
  /* The saved budget open on the page (null: a new one, not saved yet), and its name. */
  const [opened, setOpened] = useState<SavedBudget | null>(null);
  const [name, setName] = useState('');
  /* The settings and name as last saved or opened, to tell whether they have changed since. */
  const [savedAs, setSavedAs] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [show, setShow] = useState<'plan' | 'all'>('plan');

  const assets = useMemo(() => (assetsRes.data?.assets ?? []).filter((a) => a.active), [assetsRes.data]);
  const services = servicesRes.data?.services ?? [];
  const types = useMemo(() => [...new Set(assets.map((a) => a.type ?? UNTYPED))].sort(), [assets]);

  /* Fill in every type and service that has no setting yet, so each field
     shows the value the plan is actually using. */
  const fill = (s: Settings): Settings => {
    const next = { ...s, types: { ...s.types }, assets: { ...s.assets }, services: { ...s.services } };
    for (const t of types) if (!next.types[t]) next.types[t] = { lifecycleYears: suggestedLifecycle(t), price: null };
    for (const sv of services) {
      const st = next.services[sv.id];
      if (!st) next.services[sv.id] = { include: perYearOf(sv.period) > 0, increasePct: SERVICE_RISE };
      /* A budget saved before the default rise gets it, so the rate is saved with the figures. */
      else if (st.increasePct == null) next.services[sv.id] = { ...st, increasePct: SERVICE_RISE };
    }
    return next;
  };
  const snapshot = (s: Settings | null, n: string) => JSON.stringify({ s, n: n.trim() });
  const blank = (): Settings => ({ startYear: defaultStartYear(), types: {}, assets: {}, services: {} });
  const newName = () => `Budget ${savedOn(new Date().toISOString())}`;

  /* The first time in, open the budget saved last, or start a new one. */
  const [started, setStarted] = useState(false);
  useEffect(() => {
    /* After the services too, so what is filled in for them is not taken for a change. */
    if (started || !assetsRes.data || !saved.data || servicesRes.loading) return;
    setStarted(true);
    const last = saved.data[0];
    if (last) void openSaved(last.id, true);
    else {
      setSettings(fill(blank()));
      setName(newName());
      setSavedAs(null);
    }
  }, [started, assetsRes.data, saved.data, servicesRes.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  /* A device type or contract service that turns up later gets its defaults too. */
  useEffect(() => {
    setSettings((cur) => (cur ? fill(cur) : cur));
  }, [assetsRes.data, servicesRes.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = settings?.startYear ?? defaultStartYear();
  const years = Array.from({ length: YEARS }, (_, i) => start + i);

  const devices = useMemo(() => {
    if (!settings) return [];
    return assets.map((a) => {
      const type = a.type ?? UNTYPED;
      const t = settings.types[type];
      const own = settings.assets[a.id];
      const price = own?.price ?? t?.price ?? null;
      const excluded = t?.include === false;
      const p = excluded
        ? { years: [] as number[], due: null, overdue: false, unknown: false }
        : plan(a, t?.lifecycleYears ?? null, own?.replaceYear, start);
      return { asset: a, type, price, ownPrice: own?.price ?? null, override: own?.replaceYear ?? null, excluded, ...p };
    });
  }, [assets, settings, start]);

  const serviceLines = useMemo(() => {
    if (!settings) return [];
    return services.map((sv) => {
      const st = settings.services[sv.id] ?? { include: perYearOf(sv.period) > 0, increasePct: SERVICE_RISE };
      const annual = (sv.total ?? 0) * perYearOf(sv.period);
      /* A cleared field is the default rise, as the placeholder says. */
      const rise = st.increasePct ?? SERVICE_RISE;
      /* Year one at today's price, then compounding: ×1.03, ×1.03², … */
      const byYear = years.map((_, i) => (st.include ? annual * (1 + rise / 100) ** i : 0));
      return { sv, st, rise, annual, byYear };
    });
  }, [services, settings, start]); // eslint-disable-line react-hooks/exhaustive-deps

  /* The plan by type: devices and cost in each year. */
  const byType = useMemo(
    () =>
      types.map((type) => {
        const ds = devices.filter((d) => d.type === type);
        const counts = years.map((y) => ds.filter((d) => d.years.includes(y)).length);
        const costs = years.map((y) => ds.filter((d) => d.years.includes(y)).reduce((s, d) => s + (d.price ?? 0), 0));
        const unpriced = ds.filter((d) => d.years.length > 0 && d.price === null).length;
        const included = settings?.types[type]?.include !== false;
        return { type, included, devices: ds, counts, costs, unpriced, unknown: ds.filter((d) => d.unknown).length };
      }),
    [types, devices, settings], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const hardware = years.map((_, i) => byType.reduce((s, t) => s + at(t.costs, i), 0));
  const recurring = years.map((_, i) => serviceLines.reduce((s, l) => s + at(l.byYear, i), 0));
  /* Declined work is not going to be bought, whatever year it was ticked into. */
  const recCandidates = recommendations.filter((r) => r.Status !== 'Declined');
  const recLines = recCandidates
    .filter((r) => r.BudgetYear)
    .map((r) => ({ rec: r, byYear: budgetByYear(r, years) }))
    .sort((a, b) => (a.rec.BudgetYear ?? 0) - (b.rec.BudgetYear ?? 0) || (a.rec.Title ?? '').localeCompare(b.rec.Title ?? ''));
  const recommended = years.map((_, i) => recLines.reduce((s, l) => s + at(l.byYear, i), 0));
  const totals = years.map((_, i) => at(hardware, i) + at(recurring, i) + at(recommended, i));
  const grand = totals.reduce((a, b) => a + b, 0);
  const unpriced = byType.reduce((s, t) => s + t.unpriced, 0);
  const unknown = byType.reduce((s, t) => s + t.unknown, 0);

  /* A new budget is unsaved until it is saved. */
  const dirty = settings !== null && (savedAs === null || savedAs !== snapshot(settings, name));
  const leaveOk = () => !dirty || window.confirm('Leave this budget? The changes since it was saved are lost.');

  async function openSaved(id: string, first = false) {
    if (!first && !leaveOk()) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = await api.get<SavedBudget>(`/am/clients/${clientId}/budgets/${id}`);
      const s = fill(r.settings ?? blank());
      setSettings(s);
      setOpened(r);
      setName(r.name);
      /* Against the filled-in settings, so a type nobody had set yet is not taken for a change. */
      setSavedAs(snapshot(s, r.name));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not open the budget');
      /* The first time in, something to work on even so. */
      if (first) setSettings((cur) => cur ?? fill(blank()));
    } finally {
      setBusy(false);
    }
  }

  /** A new budget, from the suggested lifecycles. */
  function startNew() {
    if (!leaveOk()) return;
    setSettings(fill(blank()));
    setOpened(null);
    setName(newName());
    setSavedAs(null);
    setSaveError(null);
  }

  async function renameSaved(r: SavedBudget) {
    const next = window.prompt('Rename the budget', r.name)?.trim();
    if (!next || next === r.name) return;
    setSaveError(null);
    try {
      const u = await api.put<SavedBudget>(`/am/clients/${clientId}/budgets/${r.id}`, { name: next });
      /* If it is open, its name on the page follows; any other change stays unsaved. */
      if (opened?.id === r.id) {
        setOpened(u);
        setName(u.name);
        setSavedAs((cur) => (cur === null ? cur : JSON.stringify({ ...(JSON.parse(cur) as object), n: u.name })));
      }
      saved.reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not rename the budget');
    }
  }

  async function deleteSaved(r: SavedBudget) {
    if (!window.confirm(`Delete the saved budget "${r.name}"?`)) return;
    setSaveError(null);
    try {
      await api.del(`/am/clients/${clientId}/budgets/${r.id}`);
      /* If it was open, the page keeps its settings, as a new budget. */
      if (opened?.id === r.id) {
        setOpened(null);
        setSavedAs(null);
      }
      saved.reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not delete the budget');
    }
  }

  if (assetsRes.loading || (saved.loading && !saved.data)) return <Loading label="Reading assets and contracts from Autotask" />;
  if (assetsRes.error) return <ErrorBanner message={assetsRes.error} />;
  if (saved.error && !saved.data) return <ErrorBanner message={saved.error} />;
  if (!assetsRes.data?.linked) {
    return (
      <Empty title="Not found in Autotask">
        <p className="muted">
          {clientName} could not be found in Autotask under its name here, so there are no assets or contracts to budget for.
        </p>
      </Empty>
    );
  }
  if (!settings) return <Loading label="Building the plan" />;

  const setType = (type: string, patch: Partial<Settings['types'][string]>) =>
    setSettings({
      ...settings,
      types: { ...settings.types, [type]: { lifecycleYears: null, price: null, ...settings.types[type], ...patch } },
    });
  const setAsset = (id: number, patch: Settings['assets'][string]) => {
    const merged = { ...settings.assets[id], ...patch };
    const next = { ...settings.assets };
    if ((merged.price ?? null) === null && (merged.replaceYear ?? null) === null) delete next[id];
    else next[id] = merged;
    setSettings({ ...settings, assets: next });
  };
  const setService = (id: number, patch: Partial<Settings['services'][string]>) =>
    setSettings({ ...settings, services: { ...settings.services, [id]: { ...settings.services[id], ...patch } } });

  /** Save over the budget that is open, or as a new one. */
  const save = async (asNew: boolean) => {
    if (!name.trim()) {
      setSaveError('Give the budget a name.');
      return;
    }
    setBusy(true);
    setSaveError(null);
    /* Saved as new under the same name, it is told apart as a copy. */
    const n = asNew && opened && name.trim() === opened.name ? `${name.trim()} (copy)` : name.trim();
    const payload = { name: n, settings };
    try {
      const r =
        opened && !asNew
          ? await api.put<SavedBudget>(`/am/clients/${clientId}/budgets/${opened.id}`, payload)
          : await api.post<SavedBudget>(`/am/clients/${clientId}/budgets`, payload);
      setOpened(r);
      setName(r.name);
      setSavedAs(snapshot(settings, r.name));
      saved.reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'The budget could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const shownDevices = devices
    .filter((d) => (!typeFilter || d.type === typeFilter) && (show === 'all' || d.years.length > 0))
    .sort((a, b) => (a.years[0] ?? 9999) - (b.years[0] ?? 9999) || a.type.localeCompare(b.type) || a.asset.name.localeCompare(b.asset.name));

  const exportCsv = () =>
    downloadCsv(`${slugForFile(clientName)}_5_year_budget_${fileStamp()}.csv`, [
      ['Section', 'Item', 'Detail', ...years.map(String), 'Total'],
      ...byType.filter((t) => t.included).map((t) => ['Hardware replacement', t.type, `${t.devices.length} active · ${settings.types[t.type]?.lifecycleYears ?? '—'} yr lifecycle`, ...t.costs, t.costs.reduce((a, b) => a + b, 0)]),
      ...serviceLines
        .filter((l) => l.st.include)
        .map((l) => ['Contract service', l.sv.service, `${l.sv.contract} · +${l.rise}% a year`, ...l.byYear.map((v) => Math.round(v * 100) / 100), Math.round(l.byYear.reduce((a, b) => a + b, 0) * 100) / 100]),
      ...recLines.map((l) => ['Recommendation', l.rec.Title ?? '', `From ${l.rec.BudgetYear} · ${costSummaryText(l.rec)}`, ...l.byYear, l.byYear.reduce((a, b) => a + b, 0)]),
      ['Total', '', '', ...totals.map((v) => Math.round(v * 100) / 100), Math.round(grand * 100) / 100],
      [],
      ['Device', 'Type', 'Installed', 'Due', 'Replace in', 'Price'],
      ...devices
        .filter((d) => d.years.length > 0)
        .map((d) => [d.asset.name, d.type, d.asset.installed, d.due, d.years.join(' & '), d.price]),
    ]);

  return (
    <div className="mt-4">
      <div className="no-print">
      {/* Saved budgets */}
      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Saved budgets</h3>
            <p className="sub">Each Save keeps the budget under its name. Open one to look at it or carry on with it.</p>
          </div>
        </div>
        {saved.error ? (
          <div className="card-body"><ErrorBanner message={saved.error} /></div>
        ) : !saved.data?.length ? (
          <div className="card-body"><p className="muted small">None yet. Name the budget below and save it.</p></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Budget</th>
                  <th>Plan</th>
                  <th>Saved</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {saved.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <button type="button" className="link-button" disabled={busy} onClick={() => void openSaved(r.id)}>
                        {r.name}
                      </button>
                      {opened?.id === r.id && <span className="xs muted"> · open below</span>}
                    </td>
                    <td className="xs">{r.startYear ? `${r.startYear}–${r.startYear + YEARS - 1}` : '—'}</td>
                    <td className="xs">
                      {savedOn(r.updatedUtc)}
                      {r.updatedBy && <div className="muted">{r.updatedBy}</div>}
                    </td>
                    <td className="xs">
                      {savedOn(r.createdUtc)}
                      {r.createdBy && <div className="muted">{r.createdBy}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="link-button xs" onClick={() => void renameSaved(r)}>
                        Rename
                      </button>
                      {' · '}
                      <button type="button" className="link-button xs" onClick={() => void deleteSaved(r)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Five-year budget{opened ? ` — ${opened.name}` : ''}</h3>
            <p className="sub">
              {assets.length} active asset{assets.length === 1 ? '' : 's'} and {services.length} contract service
              {services.length === 1 ? '' : 's'} for <strong>{assetsRes.data.companyName}</strong>
              {unpriced ? ` · ${unpriced} device${unpriced === 1 ? '' : 's'} due with no price` : ''}
              {unknown ? ` · ${unknown} with no install date (give them a year below)` : ''}
              {opened
                ? ` · saved ${savedOn(opened.updatedUtc)}${opened.updatedBy ? ` by ${opened.updatedBy}` : ''}${dirty ? ', changed since' : ''}`
                : ' · not saved yet'}
            </p>
          </div>
          <div className="row wrap" style={{ alignItems: 'flex-end' }}>
            <Field label="Plan starts">
              <select
                className="select"
                value={start}
                onChange={(e) => setSettings({ ...settings, startYear: Number(e.target.value) })}
              >
                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 1 + i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Field>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={assetsRes.refreshing || servicesRes.refreshing}
              onClick={() => {
                assetsRes.refresh();
                servicesRes.refresh();
              }}
            >
              {assetsRes.refreshing || servicesRes.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>
              Print / save PDF
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="cr-save">
            <label className="cr-save-name">
              <span className="xs muted">Name</span>
              <input className="input input-sm" value={name} maxLength={200} placeholder="e.g. 2027 plan, option A" onChange={(e) => setName(e.target.value)} />
            </label>
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !dirty} onClick={() => void save(false)}>
                {busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}
              </button>
              {opened && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void save(true)}>
                  Save as new
                </button>
              )}
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={startNew} title="Start a new budget from the suggested lifecycles">
                New budget
              </button>
            </div>
          </div>
          {saveError && <ErrorBanner message={saveError} />}
          {servicesRes.error && <ErrorBanner message={`Contract services could not be read: ${servicesRes.error}`} />}
          <div className="grid grid-4 mb-4">
            <div className="stat">
              <span className="label">Five-year total</span>
              <div className="figure">{money(grand)}</div>
              <span className="foot">
                {years[0]}–{years[YEARS - 1]}
              </span>
            </div>
            <div className="stat">
              <span className="label">Hardware replacement</span>
              <div className="figure">{money(hardware.reduce((a, b) => a + b, 0))}</div>
              <span className="foot">{devices.reduce((s, d) => s + d.years.length, 0)} replacements</span>
            </div>
            <div className="stat">
              <span className="label">Contract services</span>
              <div className="figure">{money(recurring.reduce((a, b) => a + b, 0))}</div>
              <span className="foot">{serviceLines.filter((l) => l.st.include).length} services in the plan
                {serviceLines.some((l) => l.st.include && l.rise) ? ` · incl. the yearly rise` : ''}
              </span>
            </div>
            <div className="stat">
              <span className="label">Recommendations</span>
              <div className="figure">{money(recommended.reduce((a, b) => a + b, 0))}</div>
              <span className="foot">
                {recLines.length} of {recCandidates.length} in the plan
              </span>
            </div>
            <div className="stat">
              <span className="label">Average a year</span>
              <div className="figure">{money(grand / YEARS)}</div>
              <span className="foot">{money(grand / YEARS / 12)} a month</span>
            </div>
          </div>
          <BarList rows={years.map((y, i) => ({ name: String(y), sub: `${money(at(hardware, i))} hardware${at(recommended, i) ? ` · ${money(at(recommended, i))} recommended` : ''}`, value: at(totals, i), display: money(at(totals, i)) }))} />
        </div>
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>The plan, year by year</h3>
            <p className="sub">
              Hardware by asset type, the services on the active contracts, then the recommendations. Untick a line to leave it
              out of the budget — the totals, the CSV and the PDF follow.
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                {years.map((y) => (
                  <th key={y} className="num">
                    {y}
                  </th>
                ))}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {byType.map((t) => (
                <tr key={t.type} className={t.included ? undefined : 'muted'}>
                  <td>
                    <label className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                      <input
                        type="checkbox"
                        aria-label={`Include ${t.type} in the budget`}
                        checked={t.included}
                        onChange={(e) => setType(t.type, { include: e.target.checked })}
                      />
                      <span>
                        <div className="strong">{t.type}</div>
                        <div className="xs muted">
                          {t.devices.length} active · {t.included ? `replace every ${settings.types[t.type]?.lifecycleYears ?? '—'} yr` : 'left out'}
                        </div>
                      </span>
                    </label>
                  </td>
                  {t.costs.map((c, i) => (
                    <td key={i} className="num">
                      {t.counts[i] ? money(c) : '—'}
                      {at(t.counts, i) > 0 && <div className="xs muted">{t.counts[i]} device{t.counts[i] === 1 ? '' : 's'}</div>}
                    </td>
                  ))}
                  <td className="num strong">{money(t.costs.reduce((a, b) => a + b, 0))}</td>
                </tr>
              ))}
              <tr className="cr-total">
                <td>Hardware replacement</td>
                {hardware.map((v, i) => (
                  <td key={i} className="num">
                    {money(v)}
                  </td>
                ))}
                <td className="num">{money(hardware.reduce((a, b) => a + b, 0))}</td>
              </tr>
              {serviceLines.map((l) => (
                  <tr key={l.sv.id} className={l.st.include ? undefined : 'muted'}>
                    <td>
                      <label className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                        <input
                          type="checkbox"
                          aria-label={`Include ${l.sv.service} in the budget`}
                          checked={l.st.include}
                          onChange={(e) => setService(l.sv.id, { include: e.target.checked })}
                        />
                        <span>
                          <div className="strong">{l.sv.service}</div>
                          <div className="xs muted">
                            {l.sv.contract}
                            {!l.st.include ? ' · left out' : l.rise ? ` · +${l.rise}% a year from ${years[1]}` : ' · no rise'}
                          </div>
                        </span>
                      </label>
                    </td>
                    {l.byYear.map((v, i) => (
                      <td key={i} className="num">
                        {money(v)}
                      </td>
                    ))}
                    <td className="num strong">{money(l.byYear.reduce((a, b) => a + b, 0))}</td>
                  </tr>
                ))}
              <tr className="cr-total">
                <td>Contract services</td>
                {recurring.map((v, i) => (
                  <td key={i} className="num">
                    {money(v)}
                  </td>
                ))}
                <td className="num">{money(recurring.reduce((a, b) => a + b, 0))}</td>
              </tr>
              {recCandidates.map((r) => ({ rec: r, byYear: budgetByYear(r, years) })).map((l) => (
                <tr key={l.rec.AmRecommendationId} className={l.rec.BudgetYear ? undefined : 'muted'}>
                  <td>
                    <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                      <BudgetYearPicker rec={l.rec} onSaved={reload} years={years} />
                      <span>
                        <div className="strong">{l.rec.Title}</div>
                        <div className="xs muted">Recommendation · {costSummaryText(l.rec)}</div>
                      </span>
                    </div>
                  </td>
                  {l.byYear.map((v, i) => (
                    <td key={i} className="num">
                      {v ? money(v) : '—'}
                    </td>
                  ))}
                  <td className="num strong">{money(l.byYear.reduce((a, b) => a + b, 0))}</td>
                </tr>
              ))}
              <tr className="cr-total">
                <td>Recommendations</td>
                {recommended.map((v, i) => (
                  <td key={i} className="num">
                    {money(v)}
                  </td>
                ))}
                <td className="num">{money(recommended.reduce((a, b) => a + b, 0))}</td>
              </tr>
              <tr className="cr-total">
                <td>Total</td>
                {totals.map((v, i) => (
                  <td key={i} className="num">
                    {money(v)}
                  </td>
                ))}
                <td className="num">{money(grand)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Lifecycle and price by asset type</h3>
            <p className="sub">
              How many years a device of each type is kept, and what replacing one costs. A device counts its age from its
              install date in Autotask; a price set on one device below wins over the type's.
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Asset type</th>
                <th className="num">Active</th>
                <th className="num">Lifecycle (years)</th>
                <th className="num">Price per device</th>
                <th className="num">Due in plan</th>
                <th className="num">Five-year cost</th>
              </tr>
            </thead>
            <tbody>
              {byType.map((t) => {
                const st = settings.types[t.type];
                return (
                  <tr key={t.type}>
                    <td className="strong">{t.type}</td>
                    <td className="num">{t.devices.length}</td>
                    <td className="num">
                      <input
                        className="input input-sm cr-num"
                        type="number"
                        min={0.5}
                        step={0.5}
                        aria-label={`Lifecycle for ${t.type}`}
                        value={st?.lifecycleYears ?? ''}
                        onChange={(e) => setType(t.type, { lifecycleYears: numOrNull(e.target.value) })}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="input input-sm cr-num"
                        style={{ width: 100 }}
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="Set price"
                        aria-label={`Replacement price for ${t.type}`}
                        value={st?.price ?? ''}
                        onChange={(e) => setType(t.type, { price: numOrNull(e.target.value) })}
                      />
                    </td>
                    <td className="num">
                      {t.counts.reduce((a, b) => a + b, 0)}
                      {t.unpriced > 0 && <div className="xs" style={{ color: 'var(--status-danger)' }}>{t.unpriced} unpriced</div>}
                    </td>
                    <td className="num strong">{money(t.costs.reduce((a, b) => a + b, 0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Devices to replace</h3>
            <p className="sub">
              When each device falls due and what it costs. Set a price on one device to override its type, or pick the year
              it is replaced — or leave it out of the plan.
            </p>
          </div>
          <div className="row wrap" style={{ alignItems: 'flex-end' }}>
            <Field label="Type">
              <select className="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Show">
              <Segmented
                label="Show"
                options={[
                  { id: 'plan', label: `In the plan (${devices.filter((d) => d.years.length > 0).length})` },
                  { id: 'all', label: `All (${devices.length})` },
                ]}
                value={show}
                onChange={setShow}
              />
            </Field>
          </div>
        </div>
        {shownDevices.length === 0 ? (
          <div className="card-body">
            <p className="muted small">{devices.length === 0 ? 'Autotask has no active configuration items for this client.' : 'Nothing matches.'}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Type</th>
                  <th>Installed</th>
                  <th>Warranty</th>
                  <th>Due</th>
                  <th>Replace in</th>
                  <th className="num">Price</th>
                  <th className="num">Five-year cost</th>
                </tr>
              </thead>
              <tbody>
                {shownDevices.map((d) => (
                  <tr key={d.asset.id}>
                    <td>
                      <div className="strong">{d.asset.name}</div>
                      <div className="xs muted">{[d.asset.product, d.asset.lastUser ?? d.asset.contact].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td>{d.type}</td>
                    <td className="xs">{day(d.asset.installed)}</td>
                    <td className="xs">{day(d.asset.warrantyExpires)}</td>
                    <td className="xs">
                      {d.excluded ? (
                        <span className="muted">Type left out</span>
                      ) : d.unknown ? (
                        <span className="muted">{d.asset.installed ? 'No lifecycle set' : 'No install date'}</span>
                      ) : (
                        d.due ?? '—'
                      )}
                      {d.overdue && <div style={{ color: 'var(--status-danger)', fontWeight: 600 }}>Overdue</div>}
                    </td>
                    <td>
                      <select
                        className="select input-sm"
                        aria-label={`Replacement year for ${d.asset.name}`}
                        value={d.override === null ? '' : String(d.override)}
                        onChange={(e) => setAsset(d.asset.id, { replaceYear: e.target.value === '' ? null : Number(e.target.value) })}
                      >
                        <option value="">{d.unknown ? 'Not scheduled' : `By lifecycle${d.years.length ? ` (${d.years.join(', ')})` : ' (after plan)'}`}</option>
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                        <option value="0">Don't replace</option>
                      </select>
                    </td>
                    <td className="num">
                      <input
                        className="input input-sm cr-num"
                        style={{ width: 100 }}
                        type="number"
                        min={0}
                        step="0.01"
                        aria-label={`Price for ${d.asset.name}`}
                        placeholder={settings.types[d.type]?.price != null ? String(settings.types[d.type]!.price) : 'Set price'}
                        value={d.ownPrice ?? ''}
                        onChange={(e) => setAsset(d.asset.id, { price: numOrNull(e.target.value) })}
                      />
                    </td>
                    <td className="num strong">{d.years.length ? (d.price === null ? <span className="muted">No price</span> : money(d.price * d.years.length)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Contract services</h3>
            <p className="sub">
              The services on the client's active contracts, as a yearly cost. Tick what belongs in the budget. Each rises{' '}
              {SERVICE_RISE}% a year, compounding — {years[0]} at today's price, {years[1]} ×{(1 + SERVICE_RISE / 100).toFixed(2)},{' '}
              {years[2]} ×{(1 + SERVICE_RISE / 100).toFixed(2)}² and so on — unless you give it its own rate (0 for none).
            </p>
          </div>
        </div>
        {servicesRes.loading ? (
          <div className="card-body">
            <Loading label="Reading the client's contracts from Autotask" />
          </div>
        ) : services.length === 0 ? (
          <div className="card-body">
            <p className="muted small">No services on an active contract in Autotask.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>In plan</th>
                  <th>Service</th>
                  <th>Period</th>
                  <th className="num">Units</th>
                  <th className="num">Billed</th>
                  <th className="num">A year</th>
                  <th className="num">Rise a year</th>
                  <th className="num">In {years[YEARS - 1]}</th>
                </tr>
              </thead>
              <tbody>
                {serviceLines.map((l) => (
                  <tr key={l.sv.id} className={l.st.include ? undefined : 'muted'}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Include ${l.sv.service}`}
                        checked={l.st.include}
                        onChange={(e) => setService(l.sv.id, { include: e.target.checked })}
                      />
                    </td>
                    <td>
                      <div className="strong">{l.sv.service}</div>
                      <div className="xs muted">{l.sv.contract}</div>
                    </td>
                    <td>{l.sv.period ?? '—'}</td>
                    <td className="num">{l.sv.units === null ? '—' : formatNumber(l.sv.units)}</td>
                    <td className="num">{l.sv.total === null ? '—' : money(l.sv.total)}</td>
                    <td className="num strong">{perYearOf(l.sv.period) === 0 ? <span className="muted">One-time</span> : money(l.annual)}</td>
                    <td className="num">
                      <input
                        className="input input-sm cr-num"
                        type="number"
                        step="0.5"
                        placeholder={String(SERVICE_RISE)}
                        aria-label={`Yearly rise for ${l.sv.service}`}
                        value={l.st.increasePct ?? ''}
                        onChange={(e) => setService(l.sv.id, { increasePct: numOrNull(e.target.value) })}
                      />
                      %
                    </td>
                    {/* The last year of the plan, with the rise compounded in. */}
                    <td className="num">
                      {!l.st.include || perYearOf(l.sv.period) === 0 ? <span className="muted">—</span> : money(at(l.byYear, YEARS - 1))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Recommendations</h3>
            <p className="sub">
              Tick a recommendation into the budget and pick its year: a one-time cost lands in that year, a monthly or annual
              cost runs every year from then on. Saved straight away, and the same tick as on the Recommendations tab.
            </p>
          </div>
        </div>
        {recCandidates.length === 0 ? (
          <div className="card-body">
            <p className="muted small">No recommendations for this client{recommendations.length ? ' that are not declined' : ''}.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>In budget · year</th>
                  <th>Recommendation</th>
                  <th>Status</th>
                  <th>Price</th>
                  <th className="num">In the plan</th>
                </tr>
              </thead>
              <tbody>
                {recCandidates.map((r) => {
                  const inPlan = budgetByYear(r, years).reduce((a, b) => a + b, 0);
                  return (
                    <tr key={r.AmRecommendationId} className={r.BudgetYear ? undefined : 'muted'}>
                      <td>
                        <BudgetYearPicker rec={r} onSaved={reload} years={years} />
                      </td>
                      <td>
                        <div className="strong">{r.Title}</div>
                        {r.Finding && <div className="xs muted">{r.Finding}</div>}
                      </td>
                      <td>{r.Status ?? 'Proposed'}</td>
                      <td className="xs">{costSummaryText(r)}</td>
                      <td className="num strong">
                        {r.BudgetYear ? money(inPlan) : '—'}
                        {r.BudgetYear && (r.BudgetYear < years[0]! || r.BudgetYear > years[YEARS - 1]!) && (
                          <div className="xs muted">{r.BudgetYear} is outside the plan</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <h3 className="mt-6 mb-2">The document</h3>
      <p className="xs muted mb-2">
        What prints, on one landscape page: the plan by year, hardware by type and the contract services in the plan. The
        device-by-device detail stays here. A long plan is scaled down to fit.
      </p>
      </div>

      <BudgetDocument
        organizationName={organizationName}
        clientName={clientName}
        years={years}
        byType={byType.filter((t) => t.included)}
        lifecycles={Object.fromEntries(types.map((t) => [t, settings.types[t]?.lifecycleYears ?? null]))}
        services={serviceLines
          .filter((l) => l.st.include)
          .map((l) => ({ id: l.sv.id, name: l.sv.service, contract: l.sv.contract, rise: l.rise, byYear: l.byYear }))}
        hardware={hardware}
        recurring={recurring}
        recs={recLines.map((l) => ({ id: l.rec.AmRecommendationId, title: l.rec.Title ?? 'Recommendation', from: l.rec.BudgetYear!, summary: costSummaryText(l.rec), byYear: l.byYear }))}
        recommended={recommended}
        totals={totals}
      />
    </div>
  );
}

/* =========================================================================
   The printable document
   ========================================================================= */

const sum = (row: number[]) => row.reduce((a, b) => a + b, 0);

/**
 * The budget as it is handed to the client: one landscape page, laid out and
 * scaled to fit exactly as the business case is (useFitToSheet and the
 * .bc-doc sheet rules) — the years summarised beside what the plan assumes,
 * then every line of the plan by year. The devices one by one stay on screen:
 * a client with sixty workstations would be pages of serial numbers.
 */
function BudgetDocument({
  organizationName,
  clientName,
  years,
  byType,
  lifecycles,
  services,
  hardware,
  recurring,
  recs,
  recommended,
  totals,
}: {
  organizationName: string;
  clientName: string;
  years: number[];
  byType: { type: string; devices: unknown[]; counts: number[]; costs: number[] }[];
  lifecycles: Record<string, number | null>;
  services: { id: number; name: string; contract: string; rise: number | null; byYear: number[] }[];
  hardware: number[];
  recurring: number[];
  recs: { id: string; title: string; from: number; summary: string; byYear: number[] }[];
  recommended: number[];
  totals: number[];
}) {
  const ref = useFitToSheet();
  /* The recommendations column and section only when something is in the budget. */
  const withRecs = recs.length > 0;
  const first = years[0];
  const last = years[years.length - 1];
  const deviceCount = byType.reduce((n, t) => n + t.devices.length, 0);
  const replacements = byType.reduce((n, t) => n + sum(t.counts), 0);
  const cols = years.length + 2;
  const average = sum(totals) / years.length;
  /* A section's heading row carries its subtotals, as the business case's groups do. */
  const groupRow = (label: string, row: number[]) => (
    <tr className="bc-doc-group">
      <td>
        <strong>{label}</strong>
      </td>
      {row.map((v, i) => (
        <td key={i} className="num">
          <strong>{money(v)}</strong>
        </td>
      ))}
      <td className="num">
        <strong>{money(sum(row))}</strong>
      </td>
    </tr>
  );
  return (
    <article className="am-doc bc-doc" ref={ref}>
      <header className="am-doc-head">
        <div>
          <img className="am-doc-logo" src={cmitLogo} alt="CMIT Solutions" />
          <div className="am-doc-brand">{organizationName}</div>
          <h2>
            Five-year technology budget, {first}–{last}
          </h2>
        </div>
        <div className="am-doc-meta">
          <div>
            <strong>Prepared for:</strong> {clientName}
          </div>
          <div>
            <strong>Date:</strong> {formatDate(new Date().toISOString())}
          </div>
        </div>
      </header>

      <div className="bc-doc-top">
        <div>
          <div className="am-doc-title">The plan by year</div>
          <table className="am-doc-table">
            <thead>
              <tr>
                <th>Year</th>
                <th className="num">Hardware</th>
                <th className="num">Contract services</th>
                {withRecs && <th className="num">Recommended</th>}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {years.map((y, i) => (
                <tr key={y}>
                  <td>{y}</td>
                  <td className="num">{money(at(hardware, i))}</td>
                  <td className="num">{money(at(recurring, i))}</td>
                  {withRecs && <td className="num">{money(at(recommended, i))}</td>}
                  <td className="num">{money(at(totals, i))}</td>
                </tr>
              ))}
              <tr className="bc-doc-group">
                <td>
                  <strong>Five years</strong>
                </td>
                <td className="num">
                  <strong>{money(sum(hardware))}</strong>
                </td>
                <td className="num">
                  <strong>{money(sum(recurring))}</strong>
                </td>
                {withRecs && (
                  <td className="num">
                    <strong>{money(sum(recommended))}</strong>
                  </td>
                )}
                <td className="num">
                  <strong>{money(sum(totals))}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <div className="am-doc-title">What it assumes</div>
          <p className="am-doc-text">
            {deviceCount} device{deviceCount === 1 ? '' : 's'} in use today, each replaced when it reaches the usual life for
            its type, counted from when it was installed: {replacements} replacement{replacements === 1 ? '' : 's'} over the
            five years. A device already past it is budgeted in {first}.
          </p>
          <p className="am-doc-text mt-2">
            {byType
              .filter((t) => lifecycles[t.type])
              .map((t) => `${t.type}: ${lifecycles[t.type]} years`)
              .join(' · ')}
          </p>
          <p className="am-doc-text mt-2">
            The services on the current agreements start at today&rsquo;s pricing
            {services.some((s) => s.rise) ? ' and rise each year after, compounding, at the rate shown beside each' : ''}.
            {withRecs &&
              ` ${recs.length} recommendation${recs.length === 1 ? ' is' : 's are'} included in the year chosen for ${recs.length === 1 ? 'it' : 'each'}: a one-time cost in that year, a monthly or annual cost every year from then on.`}{' '}
            Averaged over the five years the
            plan comes to {money(average)} a year, or {money(average / 12)} a month.
          </p>
        </div>
      </div>

      <div className="am-doc-title">Line by line</div>
      <table className="am-doc-table">
        <thead>
          <tr>
            <th>Item</th>
            {years.map((y) => (
              <th key={y} className="num">
                {y}
              </th>
            ))}
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {groupRow('Hardware replacement', hardware)}
          {byType.length === 0 && (
            <tr>
              <td colSpan={cols} className="muted">
                No devices.
              </td>
            </tr>
          )}
          {byType.map((t) => (
            <tr key={t.type}>
              <td>
                {t.type}{' '}
                <span className="muted">
                  ({t.devices.length} in use{lifecycles[t.type] ? `, every ${lifecycles[t.type]} yrs` : ''})
                </span>
              </td>
              {t.costs.map((c, i) => (
                <td key={i} className="num">
                  {at(t.counts, i) ? (
                    <>
                      {money(c)} <span className="muted">×{at(t.counts, i)}</span>
                    </>
                  ) : (
                    ''
                  )}
                </td>
              ))}
              <td className="num">{money(sum(t.costs))}</td>
            </tr>
          ))}
          {groupRow('Contract services', recurring)}
          {services.length === 0 && (
            <tr>
              <td colSpan={cols} className="muted">
                None included.
              </td>
            </tr>
          )}
          {services.map((s) => (
            <tr key={s.id}>
              <td>
                {s.name}{' '}
                <span className="muted">
                  ({s.contract}
                  {s.rise ? `, +${s.rise}% a year` : ''})
                </span>
              </td>
              {s.byYear.map((v, i) => (
                <td key={i} className="num">
                  {money(v)}
                </td>
              ))}
              <td className="num">{money(sum(s.byYear))}</td>
            </tr>
          ))}
          {withRecs && groupRow('Recommendations', recommended)}
          {recs.map((r) => (
            <tr key={r.id}>
              <td>
                {r.title}{' '}
                <span className="muted">
                  (from {r.from}, {r.summary})
                </span>
              </td>
              {r.byYear.map((v, i) => (
                <td key={i} className="num">
                  {v ? money(v) : ''}
                </td>
              ))}
              <td className="num">{money(sum(r.byYear))}</td>
            </tr>
          ))}
          {groupRow('Total', totals)}
        </tbody>
      </table>
    </article>
  );
}
